<h2 mat-dialog-title i18n="@@Organize Projects">Organize Projects</h2>

<mat-dialog-content class="op-content">

  <!-- ── Analysis options ──────────────────────────────────── -->
  <div class="op-options">

    <!--
      Title-related controls grouped together. Weight, threshold,
      and the optional extraction regex are the three knobs that
      govern how titles participate in matching; keeping them in
      a single visually-bounded fieldset makes their relationship
      obvious and avoids the operator hunting for them across the
      wider options panel.
    -->
    <fieldset class="op-title-options-group">
      <legend class="op-title-options-legend" i18n="@@Title matching">Title matching</legend>

      <div class="op-option-block">
        <label class="op-option-label" i18n="@@Title similarity weight">Title similarity weight</label>
        <div class="op-slider-row">
          <input type="range" min="0" max="100" step="5" [(ngModel)]="titleSimilarityWeight">
          <span class="op-slider-value">{{titleSimilarityWeight}}</span>
        </div>
        <div class="op-option-hint" i18n="@@Higher values give more importance to similar titles when suggesting groups">Higher values give more importance to similar titles when suggesting groups.</div>
      </div>

      <div class="op-option-block">
        <label class="op-option-label" i18n="@@Title similarity threshold">Title similarity threshold (%)</label>
        <div class="op-slider-row">
          <input type="range" min="0" max="100" step="5" [(ngModel)]="titleSimilarityThreshold">
          <span class="op-slider-value">{{titleSimilarityThreshold}}%</span>
        </div>
        <div class="op-option-hint" i18n="@@Minimum title similarity below which titles are treated as unrelated">Minimum title similarity below which titles are treated as unrelated.</div>
      </div>

      <!--
        Optional regex applied to every Book / Draft title before
        it participates in the analysis. See the info popup for
        details.
      -->
      <div class="op-option-block">
        <label class="op-option-label op-option-label-with-info">
          <span i18n="@@Title regex (optional)">Title regex (optional)</span>
          <button mat-icon-button
                  type="button"
                  class="op-info-btn"
                  [matMenuTriggerFor]="titleRegexInfo"
                  i18n-title="@@About title regex"
                  title="About title regex">
            <mat-icon>info</mat-icon>
          </button>
        </label>
        <input type="text" class="op-text-input"
               [ngModel]="titleRegex"
               (ngModelChange)="onTitleRegexChange($event)"
               placeholder="^(.+?)\s*[-—:]\s*.+$">
        <div class="op-option-hint" *ngIf="!titleRegexError" i18n="@@titleRegexHint">Extract a portion of each title before matching. Leave blank to match on the full title.</div>
        <div class="op-option-hint op-error-inline" *ngIf="titleRegexError">{{titleRegexError}}</div>

        <mat-menu #titleRegexInfo="matMenu" class="op-info-menu" xPosition="after">
          <div class="op-info-content" (click)="$event.stopPropagation()">
            <p i18n="@@titleRegexHelpIntro">Applied to each title before analysis. Only the extracted portion is used for matching and as the default Project name.</p>
            <p i18n="@@titleRegexHelpCapture">If the pattern has a capture group, group 1 is used; otherwise the whole match is used.</p>
            <p i18n="@@titleRegexHelpNoMatch">If a title does not match the pattern, the full title is used as a fallback.</p>
            <p><span i18n="@@Example:">Example:</span> <code>^(.+?)\s*[-—:]\s*.+$</code></p>
          </div>
        </mat-menu>
      </div>
    </fieldset>

    <div class="op-option-block">
      <label class="op-toggle">
        <input type="checkbox" [(ngModel)]="considerAssigned">
        <span i18n="@@Consider Assigned Books and Drafts">Consider Assigned Books and Drafts</span>
      </label>
      <div class="op-option-hint" i18n="@@When off items already in a Project are excluded from the analysis">When off, items already in a Project are excluded from the analysis.</div>
    </div>

    <!--
      Two "opens the door to modifying existing Projects" options.
      Both default OFF so the historical safe behaviour is
      preserved. Their hints spell out the difference explicitly —
      one lets the operator re-primary an existing Project, the
      other lets items get reassigned across Projects entirely.
      The `.op-option-block-caution` styling gives them a warm
      accent so it's visually clear they can rewrite existing
      catalogue relationships.
    -->
    <div class="op-option-block op-option-block-caution">
      <label class="op-toggle">
        <input type="checkbox" [(ngModel)]="suggestExistingProjectChanges">
        <span i18n="@@Suggest changes to existing Projects">Suggest changes to existing Projects</span>
      </label>
      <div class="op-option-hint" i18n="@@suggestExistingProjectChangesHint">Opens each existing Project's own composition to change — the operator can accept a new primary Book / Draft alongside the proposed additions. Off by default; existing Projects are only added to, never modified.</div>
    </div>

    <div class="op-option-block op-option-block-caution">
      <label class="op-toggle">
        <input type="checkbox" [(ngModel)]="allowReassignment">
        <span i18n="@@Consider reassigning all Books and Drafts">Consider reassigning all Books and Drafts</span>
      </label>
      <div class="op-option-hint" i18n="@@allowReassignmentHint">Allows Books and Drafts to be moved BETWEEN existing Projects when a stronger match is found. Off by default; components spanning multiple Projects are otherwise skipped as ambiguous.</div>
    </div>

    <!--
      Signal selector. Lets the operator restrict the pairwise
      scorer to only the dimension they trust as identifying a
      Project in their catalogue. Changing the selection requires
      re-running Analyze (the button lives to the right).
    -->
    <div class="op-option-block">
      <label class="op-option-label" i18n="@@Match on">Match on</label>
      <label class="op-toggle">
        <input type="radio" name="matchMode" [value]="'all'" [(ngModel)]="matchMode">
        <span i18n="@@All signals">All signals</span>
      </label>
      <label class="op-toggle">
        <input type="radio" name="matchMode" [value]="'titleOnly'" [(ngModel)]="matchMode">
        <span i18n="@@Title only">Title only</span>
      </label>
      <label class="op-toggle">
        <input type="radio" name="matchMode" [value]="'resourcesOnly'" [(ngModel)]="matchMode">
        <span i18n="@@Resources only">Resources only</span>
      </label>
      <div class="op-option-hint" i18n="@@matchModeHint">Restrict what counts as evidence that two items belong together — titles, resource_IDs, or everything.</div>
    </div>

    <div class="op-option-block">
      <label class="op-toggle">
        <input type="checkbox" [(ngModel)]="requireSameBinding">
        <span i18n="@@Require same binding type">Require same binding type</span>
      </label>
      <div class="op-option-hint" i18n="@@requireSameBindingHint">When on, only items with the same binding type may be grouped. Items with unknown binding are excluded. The binding type is appended to each proposed Project name so parallel hardcover/softcover editions get separate Projects.</div>
    </div>

    <!--
      Display-only filter. Saved proposals drop out of the list by
      default so the operator always sees only what is still
      outstanding. It deliberately does NOT participate in the
      analyze-dirty snapshot — nothing about the analysis changes
      when it's toggled, so the Analyze button must stay "up to
      date".
    -->
    <div class="op-option-block op-option-block-display">
      <label class="op-toggle">
        <input type="checkbox" [(ngModel)]="showSavedGroups">
        <span i18n="@@Show Projects after they are saved">Show Projects after they are saved</span>
      </label>
      <div class="op-option-hint" i18n="@@showSavedProjectsHint">When off, each proposal disappears from the list as soon as it has been saved, leaving only the work still outstanding. Saving is unaffected either way.</div>
    </div>

    <!--
      Analyze button. Only "filled in" (raised primary) while the
      option snapshot differs from what produced the currently
      displayed results. Once the operator has clicked Analyze and
      hasn't changed anything, it drops to a stroked / disabled
      style so it can't be confused with an actionable control.
    -->
    <div class="op-option-actions">
      <button *ngIf="analyzeIsDirty"
              mat-raised-button color="primary"
              (click)="analyze()"
              [disabled]="analyzing"
              class="op-analyze-btn op-analyze-btn-dirty">
        <mat-icon>refresh</mat-icon>
        <span i18n="@@Analyze">Analyze</span>
      </button>
      <button *ngIf="!analyzeIsDirty"
              mat-stroked-button color="primary"
              (click)="analyze()"
              [disabled]="analyzing"
              class="op-analyze-btn op-analyze-btn-clean"
              i18n-title="@@Options unchanged since last analyze"
              title="Options unchanged since last analyze">
        <mat-icon>done</mat-icon>
        <span i18n="@@Up to date">Up to date</span>
      </button>
    </div>
  </div>

  <div *ngIf="analyzeError" class="op-error">{{analyzeError}}</div>

  <!-- ── Result groups ─────────────────────────────────────── -->
  <div class="op-empty" *ngIf="!analyzing && groups.length === 0">
    <mat-icon>info</mat-icon>
    <span i18n="@@No proposed groupings found">No proposed groupings found. Try lowering the title similarity threshold, raising its weight, or enabling "Consider Assigned Books and Drafts".</span>
  </div>

  <!--
    Everything that was proposed has now been saved AND the
    saved cards are being filtered out. Without this the operator
    would be left staring at a blank panel (or, worse, at the
    "no groupings found" message, which is not what happened).
  -->
  <div class="op-empty op-empty-all-saved" *ngIf="!analyzing && groups.length > 0 && visibleGroups.length === 0">
    <mat-icon>check_circle</mat-icon>
    <span i18n="@@allProposalsSavedHint">Every proposed Project has been saved. Turn on the option to show Projects after they are saved to review them.</span>
  </div>

  <!-- Running count of saved proposals removed from view. -->
  <div class="op-saved-hidden-note" *ngIf="hiddenSavedGroupCount > 0 && visibleGroups.length > 0">
    <mat-icon>visibility_off</mat-icon>
    <span>{{hiddenSavedGroupCount | numberFormat}}</span>
    <span i18n="@@saved and hidden">saved and hidden</span>
  </div>

  <div class="op-groups" *ngIf="visibleGroups.length > 0">
    <div *ngFor="let group of visibleGroups; let gi = index; trackBy: trackGroupById"
         class="op-group"
         [class.op-group-accepted]="group.accepted"
         [class.op-group-saved]="group.saved"
         [class.op-group-target-existing]="group.targetProject_ID != null"
         [class.op-group-reassignment]="group.isReassignment"
         [class.op-group-primary-change]="hasPrimaryBookChange(group) || hasPrimaryDraftChange(group)">

      <!-- Group header -->
      <div class="op-group-header">
        <div class="op-group-title">
          <mat-icon>{{group.saved ? 'check_circle' : (group.accepted ? 'inventory' : (group.isReassignment ? 'swap_horiz' : (group.targetProject_ID != null ? 'library_add' : 'workspaces')))}}</mat-icon>
          <ng-container *ngIf="group.targetProject_ID == null">
            <span i18n="@@Proposed Project">Proposed Project</span>
            <span class="op-group-index">#{{gi + 1}}</span>
          </ng-container>
          <ng-container *ngIf="group.targetProject_ID != null && !group.isReassignment">
            <span i18n="@@Add to existing Project">Add to existing Project</span>
            <span class="op-group-target-name">{{group.targetProjectName}}</span>
            <span class="op-group-index">#{{group.targetProject_ID | numberFormat}}</span>
            <span class="op-group-target-existing"
                  *ngIf="group.targetExistingBookCount > 0 || group.targetExistingDraftCount > 0"
                  i18n-title="@@existingContentsHint"
                  title="Books and Drafts already in this Project">
              (<ng-container *ngIf="group.targetExistingBookCount > 0">{{group.targetExistingBookCount | numberFormat}} <span i18n="@@books">books</span></ng-container><ng-container *ngIf="group.targetExistingBookCount > 0 && group.targetExistingDraftCount > 0">, </ng-container><ng-container *ngIf="group.targetExistingDraftCount > 0">{{group.targetExistingDraftCount | numberFormat}} <span i18n="@@drafts">drafts</span></ng-container>)
            </span>
          </ng-container>
          <ng-container *ngIf="group.isReassignment">
            <span i18n="@@Reassign to Project">Reassign to Project</span>
            <span class="op-group-target-name">{{group.targetProjectName}}</span>
            <span class="op-group-index">#{{group.targetProject_ID | numberFormat}}</span>
          </ng-container>
          <!--
            Warning chip: a primary Book / Draft change is being
            proposed on an existing Project. Only shows when the
            effective primary differs from the Project's current
            primary — driven by `hasPrimaryBookChange` /
            `hasPrimaryDraftChange`. Warm colour + shield icon so
            it's clearly a "this modifies existing catalogue"
            marker, not a routine addition.
          -->
          <span class="op-group-warn-chip op-group-primary-change-chip"
                *ngIf="hasPrimaryBookChange(group) || hasPrimaryDraftChange(group)"
                i18n-title="@@primaryChangeChipHint"
                title="Primary Book or Draft will be changed on the existing Project">
            <mat-icon>flag</mat-icon>
            <span i18n="@@Primary change">Primary change</span>
          </span>
          <span class="op-group-counts">
            <span *ngIf="visibleBookCount(group) > 0" class="op-count-chip op-count-book">
              <mat-icon>menu_book</mat-icon>{{visibleBookCount(group) | numberFormat}}
            </span>
            <span *ngIf="visibleDraftCount(group) > 0" class="op-count-chip op-count-draft">
              <mat-icon>edit_note</mat-icon>{{visibleDraftCount(group) | numberFormat}}
            </span>
          </span>
        </div>
        <div class="op-group-actions">
          <button mat-stroked-button
                  [color]="group.accepted ? 'accent' : 'primary'"
                  (click)="toggleAccepted(group)"
                  [disabled]="group.saved">
            <mat-icon>{{group.accepted ? 'check_box' : 'check_box_outline_blank'}}</mat-icon>
            <span *ngIf="!group.accepted" i18n="@@Accept">Accept</span>
            <span *ngIf="group.accepted" i18n="@@Accepted">Accepted</span>
          </button>
          <button mat-raised-button color="primary"
                  (click)="saveGroup(group)"
                  [disabled]="!canSaveGroup(group)">
            <mat-icon>save</mat-icon>
            <span *ngIf="!group.saved && !group.saving && group.targetProject_ID == null" i18n="@@Save as Project">Save as Project</span>
            <span *ngIf="!group.saved && !group.saving && group.targetProject_ID != null && !group.isReassignment" i18n="@@Add to Project">Add to Project</span>
            <span *ngIf="!group.saved && !group.saving && group.isReassignment" i18n="@@Apply Reassignment">Apply Reassignment</span>
            <span *ngIf="group.saving" i18n="@@Saving">Saving…</span>
            <span *ngIf="group.saved" i18n="@@Saved">Saved</span>
          </button>
        </div>
      </div>

      <!-- Match reasons — most important first -->
      <div class="op-reasons" *ngIf="group.reasons.length > 0">
        <span *ngFor="let reason of group.reasons; let ri = index"
              class="op-reason-chip"
              [class.op-reason-strong]="group.reasonStrengths[ri] >= 80"
              [class.op-reason-medium]="group.reasonStrengths[ri] >= 30 && group.reasonStrengths[ri] < 80"
              [class.op-reason-weak]="group.reasonStrengths[ri] < 30">
          {{reason}}
        </span>
      </div>

      <!--
        Editable Project name — only shown for new-Project groups
        (existing-Project groups inherit the target Project's
        name). Auto-suggested from the primary Book / Draft title
        and, when `requireSameBinding` is on, appended with the
        group's binding type. Blanking the field re-enables the
        auto-suggestion.
      -->
      <div class="op-name-row" *ngIf="group.targetProject_ID == null">
        <mat-form-field appearance="outline" class="op-name-field">
          <mat-label i18n="@@Project name">Project name</mat-label>
          <input matInput type="text"
                 [ngModel]="group.proposedName"
                 (ngModelChange)="onProposedNameInput(group, $event)"
                 [disabled]="group.saved"
                 i18n-placeholder="@@Project name placeholder"
                 placeholder="Name for the new Project">
        </mat-form-field>
        <span class="op-name-hint"
              *ngIf="!group.userEditedName"
              i18n="@@Auto-suggested — edit to override">Auto-suggested — edit to override</span>
        <span class="op-name-hint op-name-hint-edited"
              *ngIf="group.userEditedName"
              i18n="@@Custom name — clear to restore suggestion">Custom name — clear to restore suggestion</span>
      </div>

      <!--
        Differences strip — the warm-colored counterpart to the
        reasons strip above. Surfaces attributes on which the
        currently-visible items in the group DISAGREE, so the
        operator can see both sides of the coin: what makes the
        group cohesive AND what still varies inside it.
      -->
      <div class="op-differences" *ngIf="getAttrAnalysis(group).differences.length > 0">
        <mat-icon class="op-differences-icon" i18n-title="@@Differences among items in this group" title="Differences among items in this group">compare_arrows</mat-icon>
        <span *ngFor="let diff of getAttrAnalysis(group).differences"
              class="op-diff-chip"
              [class.op-diff-strong]="differenceStrength(diff) >= 80"
              [class.op-diff-medium]="differenceStrength(diff) >= 50 && differenceStrength(diff) < 80"
              [class.op-diff-weak]="differenceStrength(diff) < 50">
          {{diff}}
        </span>
      </div>

      <div *ngIf="group.saveError" class="op-error">{{group.saveError}}</div>

      <!-- Items -->
      <div class="op-group-items">
        <ng-container *ngFor="let key of group.bookKeys.concat(group.draftKeys)">
          <ng-container *ngIf="getItem(key) as it">
            <div class="op-item"
                 *ngIf="!isItemHiddenInGroup(group, key)"
                 [class.op-item-book]="it.kind === 'book'"
                 [class.op-item-draft]="it.kind === 'draft'"
                 [class.op-item-primary-effective]="isEffectivePrimary(group, key)"
                 [class.op-item-primary-suggested]="isSuggestedPrimary(group, key) && !isEffectivePrimary(group, key)"
                 [class.op-item-anchor]="isAnchor(group, key)"
                 [class.op-item-cross-project]="isCrossProjectMove(group, key)">

              <!-- Type icon -->
              <mat-icon class="op-item-type-icon">
                {{it.kind === 'book' ? 'menu_book' : 'edit_note'}}
              </mat-icon>

              <!--
                Primary star. Shown for new-Project groups
                (traditional behaviour) AND for target-existing
                groups when the operator has opted into changing
                the existing Project's primary via
                `suggestExistingProjectChanges`, and for
                reassignment groups.
              -->
              <button mat-icon-button
                      class="op-item-primary-btn"
                      *ngIf="group.targetProject_ID == null || suggestExistingProjectChanges || group.isReassignment"
                      (click)="setPrimary(group, key)"
                      [disabled]="group.saved"
                      i18n-title="@@Mark as primary"
                      title="Mark as primary">
                <mat-icon *ngIf="isEffectivePrimary(group, key)">star</mat-icon>
                <mat-icon *ngIf="!isEffectivePrimary(group, key) && isSuggestedPrimary(group, key)">star_half</mat-icon>
                <mat-icon *ngIf="!isEffectivePrimary(group, key) && !isSuggestedPrimary(group, key)">star_border</mat-icon>
              </button>

              <!-- Title & metadata -->
              <div class="op-item-body">
                <!--
                  Render the FULL raw title, but visually split it
                  into three parts so the operator can see which
                  slice is actually driving matching under the
                  current Title regex:
                    - `.op-item-title-dim` on the leading and
                      trailing portions (portions NOT used for
                      matching);
                    - `.op-item-title-match` on the middle portion
                      (what the regex extracted, and what
                      `scorePair()` / `computeProposedName()` use).
                  When no regex is set (or the regex didn't match
                  the item), `titleMatchStart === 0` and
                  `titleMatchEnd === titleRaw.length`, so the
                  before/after spans collapse to empty and the
                  title renders uniformly — no visible change.
                -->
                <div class="op-item-title"
                     [class.op-meta-same]="attrMatchClass(group, key, 'title') === 'same'"
                     [class.op-meta-different]="attrMatchClass(group, key, 'title') === 'different'">
                  <ng-container *ngIf="it.titleRaw; else itemTitleUntitled"
                                ><span class="op-item-title-dim">{{it.titleRaw.substring(0, it.titleMatchStart)}}</span
                                ><span class="op-item-title-match">{{it.titleRaw.substring(it.titleMatchStart, it.titleMatchEnd)}}</span
                                ><span class="op-item-title-dim">{{it.titleRaw.substring(it.titleMatchEnd)}}</span></ng-container>
                  <ng-template #itemTitleUntitled><span class="op-item-title-dim" i18n="@@(untitled)">(untitled)</span></ng-template>
                </div>
                <div class="op-item-meta">
                  <span class="op-item-id">#{{it.id}}</span>
                  <span *ngIf="it.coverResourceId" class="op-item-meta-chip"
                        [class.op-meta-same]="attrMatchClass(group, key, 'cover') === 'same'"
                        [class.op-meta-different]="attrMatchClass(group, key, 'cover') === 'different'"
                        i18n-title="@@Cover resource" title="Cover resource">
                    <mat-icon>image</mat-icon>{{it.coverResourceId}}
                  </span>
                  <span *ngIf="it.bodyResourceId" class="op-item-meta-chip"
                        [class.op-meta-same]="attrMatchClass(group, key, 'body') === 'same'"
                        [class.op-meta-different]="attrMatchClass(group, key, 'body') === 'different'"
                        i18n-title="@@Body resource" title="Body resource">
                    <mat-icon>description</mat-icon>{{it.bodyResourceId}}
                  </span>
                  <span *ngIf="it.width && it.height" class="op-item-meta-chip"
                        [class.op-meta-same]="attrMatchClass(group, key, 'trim') === 'same'"
                        [class.op-meta-different]="attrMatchClass(group, key, 'trim') === 'different'"
                        i18n-title="@@Trim size" title="Trim size">
                    {{it.width/72 | number:'1.2-2'}}&times;{{it.height/72 | number:'1.2-2'}}
                  </span>
                  <span *ngIf="it.bindingType" class="op-item-meta-chip"
                        [class.op-meta-same]="attrMatchClass(group, key, 'binding') === 'same'"
                        [class.op-meta-different]="attrMatchClass(group, key, 'binding') === 'different'">{{it.bindingType}}</span>
                  <span *ngIf="it.laminateType" class="op-item-meta-chip"
                        [class.op-meta-same]="attrMatchClass(group, key, 'laminate') === 'same'"
                        [class.op-meta-different]="attrMatchClass(group, key, 'laminate') === 'different'">{{it.laminateType}}</span>
                  <span class="op-item-meta-chip" i18n-title="@@Last update" title="Last update">
                    {{it.lastUpdate | date:'yyyy-MM-dd'}}
                  </span>
                  <span *ngIf="it.assigned && !isAnchor(group, key) && !isCrossProjectMove(group, key)" class="op-item-meta-chip op-item-assigned" i18n="@@Already in Project">Already in Project</span>
                  <!--
                    Anchor chip — item is already a member of the
                    target Project. Only shows when the operator
                    has opted into `suggestExistingProjectChanges`
                    (or reassignment), which surfaces anchors as
                    visible items.
                  -->
                  <span *ngIf="isAnchor(group, key)" class="op-item-meta-chip op-item-anchor-chip"
                        i18n-title="@@anchorChipHint" title="Already a member of the target Project — visible so you can re-primary it or remove it from consideration">
                    <mat-icon>anchor</mat-icon><span i18n="@@In this Project">In this Project</span>
                  </span>
                  <!--
                    Current-primary chip. Says what the target
                    Project looks like TODAY, independent of any
                    primary change this group proposes (that is
                    carried by the star control and the header's
                    "Primary change" chip).
                  -->
                  <span *ngIf="isTargetPrimary(group, key)" class="op-item-meta-chip op-item-target-primary-chip"
                        i18n-title="@@currentPrimaryChipHint" title="Current primary of the existing Project">
                    <mat-icon>star</mat-icon><span i18n="@@Primary">Primary</span>
                  </span>
                  <!--
                    Cross-project move chip — item is currently on
                    a DIFFERENT Project and will be moved to the
                    target Project if the group is saved. Only
                    happens in reassignment mode. Warm colour so
                    the cross-Project move is unmistakable.
                  -->
                  <span *ngIf="isCrossProjectMove(group, key)" class="op-item-meta-chip op-item-cross-project-chip"
                        i18n-title="@@crossProjectChipHint" title="This item will be moved to the target Project from its current Project">
                    <mat-icon>swap_horiz</mat-icon><span i18n="@@Moving from">Moving from</span> {{originProjectName(group, key)}}
                  </span>
                </div>

                <!-- Also-in badges -->
                <div class="op-item-alsoin" *ngIf="otherGroupsForItem(group, key).length > 0">
                  <mat-icon>call_split</mat-icon>
                  <span i18n="@@Also in">Also in</span>
                  <ng-container *ngFor="let otherId of otherGroupsForItem(group, key)">
                    <span class="op-item-alsoin-chip">
                      {{otherId}}
                    </span>
                  </ng-container>
                </div>

                <!-- Primary-override secondary indicator -->
                <div class="op-item-suggested-note"
                     *ngIf="isSuggestedPrimary(group, key) && !isEffectivePrimary(group, key)"
                     i18n="@@Originally suggested as primary">
                  Originally suggested as primary
                </div>
              </div>

              <!--
                Split — peel this item into its own new proposed
                Project inserted immediately after the current
                group. Only offered while the group is unsaved
                (post-save the moves have already been applied on
                the server and splitting here would be
                misleading). The `call_split` icon reads as
                "branch out" which matches the operator's mental
                model of the action.
              -->
              <button mat-icon-button
                      class="op-item-split-btn"
                      (click)="splitToNewGroup(group, key)"
                      [disabled]="group.saved"
                      i18n-title="@@Split into a new proposed Project"
                      title="Split into a new proposed Project">
                <mat-icon>call_split</mat-icon>
              </button>

              <!-- Remove -->
              <button mat-icon-button
                      class="op-item-remove-btn"
                      (click)="removeFromGroup(group, key)"
                      [disabled]="group.saved"
                      i18n-title="@@Remove from group"
                      title="Remove from group">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </ng-container>
        </ng-container>

        <!-- Manually-removed items — allow restoring -->
        <ng-container *ngFor="let key of group.bookKeys.concat(group.draftKeys)">
          <ng-container *ngIf="getItem(key) as it">
            <div class="op-item op-item-removed"
                 *ngIf="group.manuallyRemovedKeys.has(key)">
              <mat-icon class="op-item-type-icon">{{it.kind === 'book' ? 'menu_book' : 'edit_note'}}</mat-icon>
              <div class="op-item-body">
                <div class="op-item-title op-item-title-removed">{{it.titleRaw || it.title || '(untitled)'}}</div>
                <div class="op-item-meta"><span class="op-item-id">#{{it.id}}</span></div>
              </div>
              <button mat-icon-button
                      (click)="restoreInGroup(group, key)"
                      [disabled]="group.saved"
                      i18n-title="@@Restore to group"
                      title="Restore to group">
                <mat-icon>restore</mat-icon>
              </button>
            </div>
          </ng-container>
        </ng-container>

        <!--
          ── Already in this Project ──────────────────────────
          Read-only listing of the Books / Drafts the target
          Project ALREADY contains, so the operator can judge the
          proposed additions against the Project's current
          contents. Rows carry the same "In this Project" chip
          used on anchors and are deliberately inert: no primary
          star, no split, no remove — nothing here is modified by
          saving the group. Collapsible, because a mature Project
          can hold a long list.
        -->
        <div class="op-existing-members"
             *ngIf="group.targetProject_ID != null && existingMemberCount(group) > 0">
          <button type="button"
                  class="op-existing-header"
                  (click)="toggleExistingMembers(group)"
                  i18n-title="@@existingMembersSectionHint"
                  title="Books and Drafts already in this Project. They are shown for context and are not changed by saving this proposal.">
            <mat-icon class="op-existing-toggle-icon">{{group.existingCollapsed ? 'chevron_right' : 'expand_more'}}</mat-icon>
            <mat-icon class="op-existing-header-icon">inventory_2</mat-icon>
            <span i18n="@@Already in this Project">Already in this Project</span>
            <span class="op-existing-count">{{existingMemberCount(group) | numberFormat}}</span>
          </button>

          <div class="op-existing-list" *ngIf="!group.existingCollapsed">
            <ng-container *ngFor="let key of existingMemberKeys(group)">
              <ng-container *ngIf="getItem(key) as it">
                <div class="op-item op-item-existing"
                     [class.op-item-book]="it.kind === 'book'"
                     [class.op-item-draft]="it.kind === 'draft'">
                  <mat-icon class="op-item-type-icon">{{it.kind === 'book' ? 'menu_book' : 'edit_note'}}</mat-icon>
                  <div class="op-item-body">
                    <div class="op-item-title">{{it.titleRaw || it.title}}<span class="op-item-title-dim" *ngIf="!it.titleRaw && !it.title" i18n="@@(untitled)">(untitled)</span></div>
                    <div class="op-item-meta">
                      <span class="op-item-id">#{{it.id}}</span>
                      <span *ngIf="it.coverResourceId" class="op-item-meta-chip"
                            i18n-title="@@Cover resource" title="Cover resource">
                        <mat-icon>image</mat-icon>{{it.coverResourceId}}
                      </span>
                      <span *ngIf="it.bodyResourceId" class="op-item-meta-chip"
                            i18n-title="@@Body resource" title="Body resource">
                        <mat-icon>description</mat-icon>{{it.bodyResourceId}}
                      </span>
                      <span *ngIf="it.width && it.height" class="op-item-meta-chip"
                            i18n-title="@@Trim size" title="Trim size">
                        {{it.width/72 | number:'1.2-2'}}&times;{{it.height/72 | number:'1.2-2'}}
                      </span>
                      <span *ngIf="it.bindingType" class="op-item-meta-chip">{{it.bindingType}}</span>
                      <span *ngIf="it.laminateType" class="op-item-meta-chip">{{it.laminateType}}</span>
                      <span class="op-item-meta-chip" i18n-title="@@Last update" title="Last update">
                        {{it.lastUpdate | date:'yyyy-MM-dd'}}
                      </span>
                      <span class="op-item-meta-chip op-item-anchor-chip"
                            i18n-title="@@existingMemberChipHint"
                            title="Already a member of this Project — not changed by this proposal">
                        <mat-icon>anchor</mat-icon><span i18n="@@In this Project">In this Project</span>
                      </span>
                      <span *ngIf="isTargetPrimary(group, key)" class="op-item-meta-chip op-item-target-primary-chip"
                            i18n-title="@@currentPrimaryChipHint" title="Current primary of the existing Project">
                        <mat-icon>star</mat-icon><span i18n="@@Primary">Primary</span>
                      </span>
                    </div>
                  </div>
                </div>
              </ng-container>
            </ng-container>
          </div>
        </div>
      </div>
    </div>
  </div>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button (click)="close()" i18n="@@Close">Close</button>
</mat-dialog-actions>

